import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useTranslation } from "../../i18n";

/**
 * Table listing all campaigns.
 *
 * The status column always shows an enabled/disabled toggle.
 * For **one-shot** types the action button label says “Launch”;
 * for cron types it says “Run”.
 *
 * `busyId` disables Run/Delete for the campaign with an in-flight action,
 * preventing double-click double-sends.
 */
export default function CampaignTable({
  campaigns,
  triggerTypes,
  selectedId,
  busyId = null,
  onSelect,
  onToggle,
  onRun,
  onTest,
  onDelete,
}) {
  const { t } = useTranslation();

  const getLabel = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.label || triggerType;
  };

  const isOneShot = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.oneShot ?? false;
  };

  if (campaigns.length === 0) {
    return (
      <div className="text-c-gray-400 text-sm py-8 text-center">
        {t("campaign.noCampaigns")}
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-c-gray-400/30">
            <th className="text-left py-2 px-2 text-c-gray-400">
              {t("campaign.colStatus")}
            </th>
            <th className="text-left py-2 px-2 text-c-gray-400">
              {t("campaign.colName")}
            </th>
            <th className="text-left py-2 px-2 text-c-gray-400">
              {t("campaign.colType")}
            </th>
            <th className="text-right py-2 px-2 text-c-gray-400">
              {t("campaign.colActions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => {
            const busy = busyId === c.id;
            const oneShot = isOneShot(c.triggerType);
            return (
              <tr
                key={c.id}
                className={`border-b border-c-gray-400/10 cursor-pointer hover:bg-c-gray-500/50 ${
                  selectedId === c.id ? "bg-accent/10" : ""
                }`}
                onClick={() => onSelect(c.id)}
              >
                <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={c.enabled}
                    disabled={busy}
                    onCheckedChange={() => onToggle(c.id, !c.enabled)}
                  />
                </td>
                <td className="py-2 px-2 truncate max-w-[200px]">
                  {c.name || <span className="text-c-gray-400">#{c.id}</span>}
                </td>
                <td className="py-2 px-2">{getLabel(c.triggerType)}</td>
                <td
                  className="py-2 px-2 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex gap-1 justify-end">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => onRun(c.id)}
                    >
                      {t(oneShot ? "campaign.launch" : "campaign.run")}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => onTest(c.id)}
                    >
                      {t("campaign.test")}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={busy}
                      onClick={() => onDelete(c.id)}
                    >
                      {t("campaign.delete")}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
